<template>
  <div class="shoes-list">
    <div class="page-header">
      <!-- MODIFICATION START: Added Back Button -->
      <div class="back-button">
        <el-button @click="goBack" icon="el-icon-arrow-left">返回</el-button>
      </div>
      <!-- MODIFICATION END -->
      <h1>品牌精選</h1>
    </div>

    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="6" animated />
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      style="margin: 20px 0;"
    />

    <el-empty
      v-if="!loading && !error && shoes.length === 0"
      description="沒有找到符合條件"
    />

    <div v-if="!loading && !error && shoes.length > 0" class="shoes-grid">
      <router-link
        v-for="shoe in shoes"
        :key="shoe.id"
        :to="`/shoes/${shoe.id}`"
        class="shoe-card"
        custom
        v-slot="{ navigate }"
      >
        <div class="shoe-card-content" @click="navigate" role="link" tabindex="0" @keyup.enter="navigate">
          <div class="shoe-image">
            <div class="related-image">
              <img :src="getShoeImage(shoe)" :alt="shoe.title" loading="lazy" />
            </div>
            <div class="click-indicator">
              <span>查看詳情</span>
            </div>
          </div>
          <div class="shoe-info">
            <h3><t-text :text="shoe.title"></t-text></h3>
            <p class="model-number"><t-text :text="shoe.model_number || '無型號'"></t-text></p>
            <p class="related-price" v-if="shoe.price">$ <t-text :text="shoe.price || ''"></t-text></p>
            <div class="tags">
              <el-tag v-for="(collectionObject, collectionName, index) in shoe.collections" :key="index" size="small">
                <t-text :text="collectionName"></t-text>
              </el-tag>
            </div>
          </div>
        </div>
      </router-link>
    </div>

    <div v-if="!loading && !error && totalCount > 0" class="pagination">
      <el-pagination
        v-model:current-page="currentPage"
        :page-size="pageSize"
        :total="totalCount"
        :pager-count="5"
        layout="prev, pager, next, jumper"
        background
        @current-change="handlePageChange"
      />
      <p class="page-info">第 {{ currentPage }} 頁，共 {{ totalPages }} 頁</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { ArrowLeftBold } from '@element-plus/icons-vue'; // 假设你仍需要这个图标

const store = useStore();
const route = useRoute();
const router = useRouter();

const currentPage = ref(1);
const pageSize = ref(20);
const backendBaseUrl = process.env.VUE_APP_BACKEND_BASE_URL || '';

// 计算属性 (保持不变)
const shoes = computed(() => store.getters['shoes/allShoes'] || []);
const loading = computed(() => store.getters['shoes/isLoading']);
const error = computed(() => store.getters['shoes/errorMessage']);
const totalCount = computed(() => store.getters['shoes/totalShoesCount'] || 0);
const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value) || 0);

// syncStateFromRouteQuery 仅更新本地 currentPage (保持不变)
const syncStateFromRouteQuery = () => {
  const query = route.query;
  currentPage.value = parseInt(query.page) || 1;
};

// fetchData (保持不变 - 假设它从 store.state.shoes.filters 读取筛选条件)
const fetchData = async (options = { refresh: false }) => {
  // paramsForAPI 会被 shoes/fetchShoes action 使用
  // shoes/fetchShoes action 应该从 store.state.shoes.filters 读取 search, kind, brand
  const paramsForStoreAction = {
    page: currentPage.value,
    limit: pageSize.value,
    refresh: options.refresh,
  };
  try {
    await store.dispatch('shoes/fetchShoes', paramsForStoreAction);
    updateURLWithCurrentState();
  } catch (err) {
    console.error('获取数据时出错:', err);
  }
};

// updateURLWithCurrentState (保持不变)
const updateURLWithCurrentState = () => {
  const query = {};
  const storeFilters = store.state.shoes.filters; // 从 store 获取当前应用的 filters

  if (storeFilters.search) query.searchquery = storeFilters.search;
  if (storeFilters.kind) query.kinds = storeFilters.kind; // 确保 store 和 URL 参数名一致
  if (storeFilters.brand) query.brands = storeFilters.brand;
  // ... 其他筛选和排序参数 ...

  if (currentPage.value > 1) {
    query.page = currentPage.value;
  } else {
    delete query.page;
  }
  if (JSON.stringify(query) !== JSON.stringify(route.query)) {
    router.replace({ query });
  }
};

// handlePageChange (保持不变)
const handlePageChange = async (newPage) => {
  currentPage.value = newPage;
  await fetchData();
};

// goBack (保持不变)
const goBack = () => {
  router.back();
};

const syncFiltersToStoreFromQuery = async (query) => {
  const filtersFromUrl = {
    search: query.searchquery || undefined,
    kind: query.kinds || undefined,
    brand: query.brands || undefined,
  };

  const currentStoreFilters = store.state.shoes.filters;
  let needsStoreUpdate = false;

  // 比较每个筛选条件
  if (filtersFromUrl.search !== currentStoreFilters.search) needsStoreUpdate = true;
  if (filtersFromUrl.kind !== currentStoreFilters.kind) needsStoreUpdate = true;
  if (filtersFromUrl.brand !== currentStoreFilters.brand) needsStoreUpdate = true;

  if (needsStoreUpdate) {
    await store.dispatch('shoes/updateFilters', filtersFromUrl);
    return true; // Store was updated
  }
  return false; // Store was not updated
};

onMounted(async () => {
  syncStateFromRouteQuery();
  // 2. 从当前路由的 query 参数同步筛选条件到 Vuex store
  // syncFiltersToStoreFromQuery 会负责比较和 dispatch（如果需要）
  await syncFiltersToStoreFromQuery(route.query);

  // 3. 然后获取数据。
  // fetchData 内部触发的 shoes/fetchShoes action 会从 Vuex store 读取已更新的 filters。
  await fetchData({ refresh: true });
});

watch(() => route.query, async (newQuery, oldQuery) => {
  if (JSON.stringify(newQuery) !== JSON.stringify(oldQuery)) {
    syncStateFromRouteQuery(); // 更新 currentPage.value (基于 newQuery.page)

    // 2. 从新的 query 同步筛选条件到 Vuex store
    await syncFiltersToStoreFromQuery(newQuery);

    // 3. 然后获取数据
    await fetchData({ refresh: true });
  }
}, { deep: true });
// --- MODIFICATION END ---


// getShoeImage (保持不变)
const getShoeImage = (shoe) => {
  if (shoe.mainImage && shoe.mainImage.startsWith('http')) {
    return shoe.mainImage;
  }
  if (shoe.mainImage) {
    return `${backendBaseUrl}/${shoe.mainImage.replace(/^\/+/, '')}`;
  }
  return 'https://via.placeholder.com/300x300?text=No+Image';
}
</script>

<style scoped>
.shoes-list {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  margin-bottom: 30px;
  text-align: center;
  position: relative; /* MODIFICATION: Added for positioning the back button */
}

.page-header h1 {
  font-size: 2.5rem;
  color: #333;
  margin: 0;
  /* MODIFICATION: Add padding if back button overlaps title on small screens or adjust title margin */
  /* padding-left: 50px;  Example: if button is ~40px wide + margin */
}

.loading-container {
  margin: 40px 0;
}

.shoes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.shoe-card {
  text-decoration: none;
  color: inherit;
  display: block;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  cursor: pointer;
  position: relative;
}

.shoe-card-content {
  display: block;
  height: 100%;
  width: 100%;
}

.shoe-card:hover, .shoe-card:focus-within {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.shoe-image {
  position: relative;
  height: 180px;
  overflow: hidden;
}

.related-image {
  height: 100%;
  overflow: hidden;
}

.related-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s;
}

.shoe-card:hover .related-image img,
.shoe-card:focus-within .related-image img {
  transform: scale(1.05);
}

.click-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  text-align: center;
  padding: 10px 0;
  font-size: 0.9rem;
  font-weight: 500;
  transform: translateY(100%);
  transition: transform 0.25s ease-in-out;
  z-index: 3;
  pointer-events: none;
}

.shoe-card:hover .click-indicator,
.shoe-card:focus-within .click-indicator {
  transform: translateY(0);
}

.shoe-info {
  padding: 15px;
}

.shoe-info h3 {
  margin: 0 0 10px 0;
  font-size: 1.1rem;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.model-number {
  color: #888;
  font-size: 0.85rem;
  margin: 5px 0;
}

.related-price {
  color: #575757;
  font-weight: bold;
  font-size: 1rem;
  margin: 5px 0;
}

.tags {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}


.pagination {
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.page-info {
  margin-top: 10px;
  color: #666;
  font-size: 0.9rem;
}

.back-button {
  position: absolute;  
  margin-bottom: 20px;
  top: 50%;
}
.back-button .el-button { /* 确保图标显示 */
  display: inline-flex;
  align-items: center;
}
.back-button .el-button .el-icon {
  margin-right: 5px;
}

@media (max-width: 768px) {
  .shoes-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }

  .page-header h1 {
    font-size: 2rem;
    /* MODIFICATION: Ensure title doesn't overlap with back button on small screens */
    margin-left: 40px; /* Adjust based on button size + desired space */
    margin-right: 40px; /* If you want symmetric spacing */
    text-align: center; /* Re-center if margin affects alignment */
  }
  .back-button {
    /* Optional: Adjust button size or padding for smaller screens if needed */
    /* font-size: 1rem; */
    /* padding: 8px; */
  }
  .shoe-info h3 {
    font-size: 1rem;
  }
}
@media (max-width: 480px) {
  .shoes-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
   .shoe-info h3 {
    font-size: 0.9rem;
  }
  .page-header h1 {
    font-size: 1.8rem; /* Further reduce title size */
    margin-left: 35px; /* Adjust for potentially smaller button or tighter layout */
    margin-right: 35px;
  }
}
</style>